import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listCustomers, type CustomerListItem, type CustomersListResponse } from "../../lib/api.js";

/**
 * Seznam klientů — hledání, filtr B2B/B2C, paginace.
 */
export function CustomersPage(): JSX.Element {
  const [data, setData] = useState<CustomersListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [type, setType] = useState<"all" | "b2b" | "b2c">("all");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    listCustomers({ search, type, limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((res) => {
        if (mounted) setData(res);
      })
      .catch((e: unknown) => {
        if (mounted) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [search, type, page]);

  const totalPages = useMemo(() => (data ? Math.ceil(data.total / PAGE_SIZE) : 0), [data]);

  function applySearch(): void {
    setSearch(searchInput.trim());
    setPage(0);
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-end justify-between gap-6 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">Klienti</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Klientská databáze — fyzické osoby (B2C) a firmy (B2B). Zatím
            {data && (
              <>
                {" "}
                <strong>{data.counts.all.toLocaleString("cs-CZ")}</strong> celkem,{" "}
                <strong>{data.counts.b2b}</strong> B2B,{" "}
                <strong>{data.counts.b2c}</strong> B2C.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex-1 min-w-[260px] relative">
          <input
            type="text"
            placeholder="Hledat: e-mail, jméno, firma, IČO, město…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applySearch();
            }}
            className="w-full px-3.5 py-2 border border-zinc-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ktm-orange focus:border-transparent text-sm"
          />
        </div>
        <button
          type="button"
          onClick={applySearch}
          className="px-4 py-2 bg-ktm-orange hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition"
        >
          Hledat
        </button>
        {(search || type !== "all") && (
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setSearchInput("");
              setType("all");
              setPage(0);
            }}
            className="text-sm text-zinc-500 hover:text-zinc-800"
          >
            Vyčistit
          </button>
        )}
        <div className="flex rounded-lg border border-zinc-300 overflow-hidden text-sm">
          {(["all", "b2c", "b2b"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setType(t);
                setPage(0);
              }}
              className={`px-3 py-2 transition ${
                type === t
                  ? "bg-ktm-black text-white"
                  : "bg-white text-zinc-600 hover:bg-zinc-50"
              }`}
            >
              {t === "all" ? "Vše" : t === "b2b" ? "Firmy (B2B)" : "Fyzické osoby"}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4">
          Chyba: {error}
        </div>
      )}

      {/* Tabulka */}
      <div className="bg-white rounded-xl ring-1 ring-zinc-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold w-16">ID</th>
                <th className="px-4 py-3 text-left font-semibold">Klient</th>
                <th className="px-4 py-3 text-left font-semibold">Kontakt</th>
                <th className="px-4 py-3 text-left font-semibold">IČO / Město</th>
                <th className="px-4 py-3 text-right font-semibold w-20">Slevy</th>
                <th className="px-4 py-3 text-right font-semibold w-28">Registrace</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">
                    Načítám…
                  </td>
                </tr>
              )}
              {!loading && data && data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">
                    Nic nenalezeno.
                  </td>
                </tr>
              )}
              {!loading &&
                data?.items.map((c) => <CustomerRow key={c.id} c={c} />)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginace */}
      {data && data.total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-zinc-500">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)} z{" "}
            {data.total.toLocaleString("cs-CZ")}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm border border-zinc-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-50"
            >
              ← Předchozí
            </button>
            <span className="text-xs text-zinc-500 px-2">
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-sm border border-zinc-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-50"
            >
              Další →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerRow({ c }: { c: CustomerListItem }): JSX.Element {
  const personName = `${c.lastName ?? ""} ${c.firstName ?? ""}`.trim();
  const primaryName = c.companyName ?? personName ?? "—";

  return (
    <tr className="hover:bg-zinc-50 transition-colors">
      <td className="px-4 py-3 text-zinc-400 font-mono text-xs">
        {c.legacyId ?? "—"}
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/admin/customers/${c.id}`}
          className="font-medium text-zinc-900 hover:text-ktm-orange transition-colors"
        >
          {primaryName}
        </Link>
        <div className="flex items-center gap-2 mt-0.5">
          {c.isB2B ? (
            <span className="inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
              B2B
            </span>
          ) : (
            <span className="inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
              B2C
            </span>
          )}
          {c.companyName && personName && (
            <span className="text-xs text-zinc-500">{personName}</span>
          )}
          {c.hasShopify && (
            <span className="inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
              ✓ Shopify
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <a
          href={`mailto:${c.email}`}
          className="text-zinc-700 hover:text-ktm-orange break-all"
        >
          {c.email}
        </a>
        {c.phone && <div className="text-xs text-zinc-500 mt-0.5">{c.phone}</div>}
      </td>
      <td className="px-4 py-3">
        {c.ico ? (
          <div className="font-mono text-xs text-zinc-700">{c.ico}</div>
        ) : (
          <div className="text-xs text-zinc-400">—</div>
        )}
        {c.city && (
          <div className="text-xs text-zinc-500 mt-0.5">
            {c.city}
            {c.country && c.country !== "CZ" && ` (${c.country})`}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {c.discountCount > 0 ? (
          <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
            {c.discountCount}
          </span>
        ) : (
          <span className="text-xs text-zinc-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right text-xs text-zinc-500 whitespace-nowrap">
        {formatDate(c.registeredAt)}
      </td>
    </tr>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("cs-CZ", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
